import React, { useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"

import Carousel from "react-material-ui-carousel"
import { Card, CardContent, Avatar, Grid, Typography } from "@material-ui/core"
import StarIcon from "@material-ui/icons/Star"
import FormatQuoteIcon from "@material-ui/icons/FormatQuote"

import { selectLanguage } from "../../redux/language/language.selectors"

import "./Reviews.scss"

const clamp = (n, min, max) => Math.max(min, Math.min(max, n))

const Stars = ({ value = 5 }) => {
  const v = clamp(Number(value) || 0, 0, 5)
  return (
    <div className="reviews-stars" aria-label={`${v} stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <StarIcon key={i} className={i < v ? "on" : "off"} />
      ))}
    </div>
  )
}

const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false)

  const text = review?.text || ""
  const short = text.length > 160 ? text.slice(0, 160).trim() + "..." : text
  const shouldToggle = text.length > 160

  return (
    <Card className="reviews-card" elevation={0}>
      <CardContent className="reviews-card-content">
        <div className="reviews-quote">
          <FormatQuoteIcon />
        </div>

        <div className="reviews-header">
          <Avatar className="reviews-avatar">
            {(review?.name || "A").trim().slice(0, 1).toUpperCase()}
          </Avatar>

          <div className="reviews-meta">
            <div className="reviews-name">{review?.name}</div>
            <div className="reviews-sub">
              {review?.title ? review.title : review?.location || ""}
            </div>
          </div>

          <div className="reviews-rating">
            <Stars value={review?.rating} />
          </div>
        </div>

        <Typography className="reviews-text">
          {expanded ? text : short}
        </Typography>

        {shouldToggle ? (
          <button
            type="button"
            className="reviews-toggle"
            onClick={() => setExpanded((s) => !s)}
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Reviews({ language }) {
  const reviews = useMemo(() => {
    if (language === "EN") {
      return [
        {
          name: "Macarena D.",
          location: "Dublin",
          rating: 5,
          text: "The class was amazing. Very practical. The teacher was amazing as well, she explained everything very well and was very patient. Definitely a course to do. Thanks.",
        },
        {
          name: "Shannon O G.",
          location: "Dublin",
          rating: 5,
          text: "Took this course on Saturday with Bartira and in five hours I was amazed at how much I learned. She truly is an incredible teacher and makes sure to be attentive to absolutely everyone in her class, amazing at keeping everyone included. Especially if there is a language barrier, she will pause and switch so that everyone can stay on the same page learning together. Can't recommend her enough!!! Wishing her all the best.",
        },
        {
          name: "Patricia L. T.",
          location: "Dublin",
          rating: 5,
          text: "The barista course was excellent! We covered both the technical and practical aspects, with great attention to detail throughout. The instructor was incredibly patient and attentive, creating a supportive learning environment. I highly recommend this course to anyone looking to start a career in this field.",
        },
        {
          name: "Raphael C.",
          location: "Dublin",
          rating: 5,
          text: "The course is very good and comprehensive. Bartira is a very pleasant instructor, attentive to questions, and teaches so well that even a complete novice can leave knowing how to prepare various coffee and beverage options. I recommend it to anyone who wants to qualify to work in coffee shops or simply to coffee lovers.",
        },
        {
          name: "Orla E.",
          location: "Dublin",
          rating: 5,
          text: "Took this course today, would definitely 100% recommend. Great atmosphere, very educational and most importantly fun. Bartira is very thorough and classes are small.",
        },
        {
          name: "Anudari E.",
          location: "Dublin",
          rating: 5,
          text: "I had a wonderful barista training experience with Bartira. She’s incredibly kind, knowledgeable, and fun to learn from. Her training sessions are both helpful and engaging, perfect for anyone looking to become a barista or simply interested in learning more about coffee. The course is reasonably priced and truly worth it. I highly recommend it to anyone with a passion for coffee!",
        },
      ]
    }

    return [
      {
        name: "Macarena D.",
        location: "Dublin",
        rating: 5,
        text: "A aula foi incrível, muito prática. A professora também foi maravilhosa, explicou tudo com muita clareza e paciência. Com certeza é um curso que vale a pena fazer. Obrigada!",
      },
      {
        name: "Shannon O G.",
        location: "Dublin",
        rating: 5,
        text: "Fiz este curso no sábado com a Bartira e, em apenas cinco horas, fiquei impressionada com o quanto aprendi. Ela é realmente uma professora incrível e se preocupa em dar atenção a todos na turma. É excelente em manter todos incluídos, especialmente quando há barreira de idioma, sempre pausando e explicando novamente para que todos acompanhem juntos. Recomendo demais! Desejo todo sucesso a ela.",
      },
      {
        name: "Patricia L. T.",
        location: "Dublin",
        rating: 5,
        text: "O curso de barista foi excelente! Abordamos tanto a parte técnica quanto a prática, com muita atenção aos detalhes. A instrutora foi extremamente paciente e atenciosa, criando um ambiente de aprendizado muito acolhedor. Recomendo fortemente para quem deseja iniciar uma carreira nessa área.",
      },
      {
        name: "Raphael C.",
        location: "Dublin",
        rating: 5,
        text: "O curso é muito bom e completo. A Bartira é uma instrutora muito agradável, atenciosa às dúvidas, e ensina de forma tão clara que até quem nunca teve contato com café sai sabendo preparar diversas bebidas. Recomendo para quem quer trabalhar em cafeterias ou simplesmente para amantes de café.",
      },
      {
        name: "Orla E.",
        location: "Dublin",
        rating: 5,
        text: "Fiz o curso hoje e recomendo 100%. Ótima atmosfera, muito educativo e, o mais importante, divertido. A Bartira é muito detalhista e as turmas são pequenas.",
      },
      {
        name: "Anudari E.",
        location: "Dublin",
        rating: 5,
        text: "Tive uma experiência maravilhosa no treinamento de barista com a Bartira. Ela é extremamente gentil, experiente e divertida de aprender. As aulas são muito úteis e envolventes, perfeitas para quem quer se tornar barista ou simplesmente aprender mais sobre café. O curso tem um preço justo e vale muito a pena. Recomendo fortemente para qualquer pessoa apaixonada por café!",
      },
    ]
  }, [language])

  const title =
    language === "EN" ? "What our students say" : "O que nossos alunos dizem"
  const subtitle =
    language === "EN"
      ? "Real feedback from customers in Ireland"
      : "Feedback real de clientes na Irlanda"

  const GOOGLE_REVIEWS_URL = "https://maps.app.goo.gl/teDQrzQibH4YCArS8"

  const sectionRef = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // ✅ fallback: show anyway
    const fallback = window.setTimeout(() => setVisible(true), 250)

    const el = sectionRef.current
    if (!el) return () => window.clearTimeout(fallback)

    // If IntersectionObserver doesn't exist, fallback will show it.
    if (!("IntersectionObserver" in window)) {
      return () => window.clearTimeout(fallback)
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    io.observe(el)

    return () => {
      window.clearTimeout(fallback)
      io.disconnect()
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      id="reviews"
      className={`reviews ${visible ? "is-visible" : ""}`}
    >
      <div className="container reviews-inner">
        <div className="reviews-heading">
          <h1>{title}</h1>
          <p>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="reviews-google-link"
              aria-label="Open Google reviews"
            >
              {subtitle}
            </a>
          </p>
        </div>

        <div className="reviews-grid">
          <Grid container spacing={2}>
            {reviews.map((r, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <div
                  className="reviews-card-wrap"
                  style={{ transitionDelay: `${Math.min(idx, 6) * 70}ms` }}
                >
                  <ReviewCard review={r} />
                </div>
              </Grid>
            ))}
          </Grid>
        </div>

        <div className="reviews-carousel">
          <Carousel
            autoPlay={true}
            timer={3500}
            animation="slide"
            indicators={true}
            navButtonsAlwaysVisible={false}
            timeout={300}
          >
            {reviews.slice(0, 6).map((r, idx) => (
              <div key={idx} className="reviews-carousel-item">
                <ReviewCard review={r} />
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    </section>
  )
}

const mapStateToProps = createStructuredSelector({
  language: selectLanguage,
})

export default connect(mapStateToProps)(Reviews)
